<template>
  <BaseCard
    :id="`plan-${employeeSizeMapping}`"
    class="plan-block cursor-pointer mx-2 max-w-xs flex-shrink-0 p-1 rounded-[4px]"
    :class="[
      isCurrent
        ? 'border-2 border-primary bg-primary-tint-6 '
        : 'border border-gray-5 rounded-none',
      isEnterprise ? 'bg-gray-7' : '',
    ]"
    :full-bleed="true"
    @click="$emit('planChange', planId)"
  >
    <div class="text-left p-5 pb-2 max-w-xs">
      <div class="flex">
        <TypeDisplay variant="mega" weight="strong" tag="h2">
          {{ title }}
        </TypeDisplay>
      </div>
      <div class="flex flex-col flex-wrap">
        <TypeBody variant="text" weight="regular" class="text-gray-3 mt-2 mb-8">
          {{ subtitle }}
        </TypeBody>
      </div>
      <TypeBody
        variant="small"
        weight="regular"
        :class="[
          isEnterprise && !isCurrent ? 'text-gray-7' : 'text-gray-3',
          {
            'text-primary-tint-6': isEnterprise && isCurrent,
          },
        ]"
      >
        Priced at
      </TypeBody>
      <TypeDisplay
        variant="tera"
        weight="strong"
        tag="p"
        :class="[
          { 'text-gray-7': isEnterprise && !isCurrent },
          {
            'text-primary-tint-6': isEnterprise && isCurrent,
          },
        ]"
      >
        <span v-if="isEnterprise">Custom</span>
        <span v-else>{{ formattedMonthlyPrice }}/mo</span>
      </TypeDisplay>
      <template>
        <TypeBody
          v-if="setupFee"
          variant="caption"
          weight="regular"
          tag="p"
          class="mb-4"
          :class="[
            { 'text-gray-7': isEnterprise && !isCurrent },
            {
              'text-primary-tint-6': isEnterprise && isCurrent,
            },
          ]"
        >
          Plus a {{ currency(setupFee).format({ precision: 0 }) }}
          one-time setup fee
        </TypeBody>
        <TypeBody
          v-else
          variant="caption"
          weight="regular"
          tag="p"
          class="mb-4"
          :class="[
            { 'text-gray-7': isEnterprise && !isCurrent },
            {
              'text-primary-tint-6': isEnterprise && isCurrent,
            },
          ]"
        >
          No Setup Fee
        </TypeBody>
      </template>
      <div v-show="isCurrent" class="w-full mt-4 mb-8">
        <BaseButton
          variant="primary"
          size="large"
          class="flex w-full"
          @click="toRegistration()"
        >
          Try Bambee Now
        </BaseButton>
      </div>
      <div v-if="!isEnterprise" :class="{ 'pt-[4.5rem]': !isCurrent }">
        <TypeBody variant="small" weight="strong" class="text-gray-3">
          Includes
        </TypeBody>
        <div
          v-for="(feature, index) in features"
          :key="index"
          class="flex flex-col mt-3"
        >
          <div class="flex flex-row items-start max-w-[15rem] mb-2">
            <component
              :is="feature.component"
              :icon="feature.icon"
              class="text-secondary mt-0.5"
            />
            <TypeBody
              variant="text"
              weight="medium"
              class="ml-2 flex flex-start"
            >
              {{ feature.text }}
            </TypeBody>
          </div>
        </div>
      </div>
    </div>
  </BaseCard>
</template>

<script>
import {
  BaseButton,
  BaseCard,
  TypeBody,
  TypeDisplay,
  TypeHeading,
} from '@bambeehr/pollen';
import { computed, defineComponent, useRouter } from '@nuxtjs/composition-api';
import useRegistration, {
  RegistrationStateNames,
} from '@/modules/SelfServiceRegistration/hooks/useRegistration';
import currency from '@bambeehr/currency';

export default defineComponent({
  name: 'PlanBlock',
  components: {
    BaseButton,
    BaseCard,
    TypeBody,
    TypeDisplay,
    TypeHeading,
  },
  props: {
    employeeSizeMapping: {
      type: String,
      default: '',
    },
    planId: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      default: 'HR Manager',
    },
    subtitle: {
      type: String,
      default: 'For HR Managers',
    },
    monthlyPrice: {
      type: Number,
      required: true,
    },
    setupFee: {
      type: Number,
      default: null,
    },
    features: {
      type: Array,
      default: () => [],
    },
    isCurrent: {
      type: Boolean,
      default: false,
    },
    isEnterprise: {
      type: Boolean,
      default: false,
    },
    maxSize: {
      type: Number,
      default: 0,
    },
    tierNumber: {
      type: Number,
      default: 0,
    },
  },
  setup(props) {
    const router = useRouter();
    const { updateRegistration } = useRegistration();
    const toRegistration = (size) => {
      updateRegistration({
        [RegistrationStateNames.NUM_EMPLOYEES]: props.employeeSizeMapping,
      });
      router.push(`/registration/company-size?size=${size}`);
    };

    const formattedMonthlyPrice = computed(() => {
      return currency(props.monthlyPrice).format({ precision: 0 });
    });

    return {
      formattedMonthlyPrice,
      toRegistration,
      currency,
    };
  },
});
</script>

<style scoped>
.plan-block {
  min-width: 320px;
}
sup {
  font-size: 75%;
  line-height: 0;
  position: relative;
  top: -0.3em;
  vertical-align: super;
}
</style>
